import { useCallback, useState } from "react";
import { useRoomContext, useTrackToggle } from "@livekit/components-react";
import { Track } from "livekit-client";
import { ConnectionState } from "livekit-client";
import { PARTICIPANT_ROLES } from "@/react/helpers/liveStreamRoomHelpers";
import {
  selectMicrophoneEnabled,
  useLiveSettingsActions,
  useLiveStore,
} from "../../store";
import { useRoom } from "../useRoom";
import { useLivekitLocalMetadata } from "./useLivekitLocalMetadata";
import { useLivekitMicrophoneCaptureOptions } from "./useLivekitMicrophoneCaptureOptions";

export const useLivekitMicrophone = () => {
  const room = useRoomContext();
  const circleRoom = useRoom();
  const { metadata: localMetadata } = useLivekitLocalMetadata();
  const isConnectedAsHost =
    room.state === ConnectionState.Connected &&
    localMetadata.role === PARTICIPANT_ROLES.HOST;
  const isPreviewMicrophoneEnabled = useLiveStore(selectMicrophoneEnabled);
  const isEnabledOnConnected =
    !!circleRoom?.mute_on_join && !circleRoom?.can_manage
      ? undefined
      : isPreviewMicrophoneEnabled;
  const { setMicrophoneEnabled } = useLiveSettingsActions();
  const { captureOptions } = useLivekitMicrophoneCaptureOptions();
  const [microphoneError, setMicrophoneError] = useState<string | null>(null);

  const {
    toggle,
    enabled: isTrackEnabled,
    pending: isMicrophonePending,
  } = useTrackToggle<Track.Source.Microphone>({
    source: Track.Source.Microphone,
    captureOptions,
    initialState: isConnectedAsHost ? isEnabledOnConnected : undefined,
  });

  const isMicrophoneEnabled = isConnectedAsHost
    ? isTrackEnabled
    : isPreviewMicrophoneEnabled;

  const toggleMicrophone = useCallback(async () => {
    try {
      if (isConnectedAsHost) {
        await toggle();
      }
      setMicrophoneEnabled(!isMicrophoneEnabled);
      setMicrophoneError(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "";
      setMicrophoneError(errorMessage);
      console.error("Could not toggle microphone:", errorMessage);
    }
  }, [isConnectedAsHost, isMicrophoneEnabled, toggle, setMicrophoneEnabled]);

  return {
    isMicrophoneEnabled,
    isMicrophonePending,
    toggleMicrophone,
    microphoneError,
  };
};
